import { createTheme, Theme } from '@mui/material/styles';
import { palette, typography, shadows, customShadows, themeConfig } from './theme-config';

// Create base theme following Minimals.cc structure
export const createCustomTheme = (mode: 'light' | 'dark' = 'light'): Theme => {
  return createTheme({
    palette: {
      mode,
      ...palette[mode],
    },
    typography: {
      ...typography,
    },
    shadows: shadows[mode] as any,
    shape: {
      borderRadius: 8,
    },
    spacing: 8,
    components: {
      // Custom component overrides following Minimals.cc style
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 8,
            padding: '8px 16px',
            minHeight: 40,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          contained: {
            boxShadow: customShadows[mode].z8,
            '&:hover': {
              boxShadow: customShadows[mode].z12,
            },
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${palette[mode].primary.main} 0%, ${palette[mode].primary.dark} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${palette[mode].primary.dark} 0%, ${palette[mode].primary.darker} 100%)`,
            },
          },
          outlined: {
            borderWidth: 1.5,
            '&:hover': {
              borderWidth: 1.5,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: customShadows[mode].card,
            '&:hover': {
              boxShadow: customShadows[mode].z16,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: customShadows[mode].z4,
          },
          elevation1: {
            boxShadow: customShadows[mode].z1,
          },
          elevation4: {
            boxShadow: customShadows[mode].z4,
          },
          elevation8: {
            boxShadow: customShadows[mode].z8,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              '& fieldset': {
                borderColor: palette[mode].grey[300],
              },
              '&:hover fieldset': {
                borderColor: palette[mode].grey[400],
              },
              '&.Mui-focused fieldset': {
                borderColor: palette[mode].primary.main,
                borderWidth: 2,
              },
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            fontWeight: 500,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderBottom: `1px solid ${palette[mode].divider}`,
            background: palette[mode].background.paper,
            color: palette[mode].text.primary,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '72px !important',
            paddingLeft: '24px !important',
            paddingRight: '24px !important',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: palette[mode].text.primary,
          },
          h1: {
            color: palette[mode].text.primary,
            fontWeight: 800,
          },
          h2: {
            color: palette[mode].text.primary,
            fontWeight: 800,
          },
          h3: {
            color: palette[mode].text.primary,
            fontWeight: 700,
          },
          h4: {
            color: palette[mode].text.primary,
            fontWeight: 700,
          },
          h5: {
            color: palette[mode].text.primary,
            fontWeight: 600,
          },
          h6: {
            color: palette[mode].text.primary,
            fontWeight: 600,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '&:hover': {
              backgroundColor: palette[mode].action.hover,
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            marginBottom: 4,
            '&:hover': {
              backgroundColor: palette[mode].action.hover,
            },
            '&.Mui-selected': {
              backgroundColor: palette[mode].action.selected,
              '&:hover': {
                backgroundColor: palette[mode].action.selected,
              },
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            boxShadow: customShadows[mode].dropdown,
            border: `1px solid ${palette[mode].divider}`,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '4px 8px',
            minHeight: 40,
            '&:hover': {
              backgroundColor: palette[mode].action.hover,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${palette[mode].divider}`,
            fontSize: '0.875rem',
          },
          head: {
            fontWeight: 600,
            backgroundColor: palette[mode].background.neutral,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
          standardSuccess: {
            backgroundColor: palette[mode].success.lighter,
            color: palette[mode].success.darker,
          },
          standardError: {
            backgroundColor: palette[mode].error.lighter,
            color: palette[mode].error.darker,
          },
          standardWarning: {
            backgroundColor: palette[mode].warning.lighter,
            color: palette[mode].warning.darker,
          },
          standardInfo: {
            backgroundColor: palette[mode].info.lighter,
            color: palette[mode].info.darker,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            minHeight: 48,
            borderRadius: 8,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            fontSize: '0.75rem',
          },
        },
      },
    },
    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
    },
  });
};

// Default light theme
export const lightTheme = createCustomTheme('light');

// Default dark theme  
export const darkTheme = createCustomTheme('dark');

// Export theme config for use in other components
export { themeConfig, customShadows } from './theme-config'; 